import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getCompanyScopedStorageKey } from '../../utils/companyScope';

const BookingContext = createContext(null);

const getEmptyBookingState = () => ({
  selectedDate: null,
  selectedDateSlotId: null,
  selectedTimeSlot: null,
  selectedTimeSlotId: null,
});

const readStoredBooking = (storageKey) => {
  if (typeof window === 'undefined') {
    return getEmptyBookingState();
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return getEmptyBookingState();
    }

    const parsed = JSON.parse(raw);
    return {
      selectedDate: parsed?.selectedDate ? new Date(parsed.selectedDate) : null,
      selectedDateSlotId: parsed?.selectedDateSlotId ?? null,
      selectedTimeSlot: parsed?.selectedTimeSlot ?? null,
      selectedTimeSlotId: parsed?.selectedTimeSlotId ?? null,
    };
  } catch (error) {
    console.warn('Failed to restore booking state:', error);
    return getEmptyBookingState();
  }
};

export const BookingProvider = ({ children }) => {
  const location = useLocation();
  const storageKey = useMemo(
    () => getCompanyScopedStorageKey('booking', location.pathname),
    [location.pathname]
  );

  const [selectedDate, setSelectedDateState] = useState(null);
  const [selectedDateSlotId, setSelectedDateSlotId] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlotState] = useState(null);
  const [selectedTimeSlotId, setSelectedTimeSlotId] = useState(null);

  useEffect(() => {
    const stored = readStoredBooking(storageKey);
    setSelectedDateState(stored.selectedDate);
    setSelectedDateSlotId(stored.selectedDateSlotId);
    setSelectedTimeSlotState(stored.selectedTimeSlot);
    setSelectedTimeSlotId(stored.selectedTimeSlotId);
  }, [storageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const hasBookingSelection = Boolean(
      selectedDate || selectedDateSlotId || selectedTimeSlot || selectedTimeSlotId
    );

    if (!hasBookingSelection) {
      window.localStorage.removeItem(storageKey);
      return;
    }

    window.localStorage.setItem(
      storageKey,
      JSON.stringify({
        selectedDate: selectedDate instanceof Date ? selectedDate.toISOString() : selectedDate,
        selectedDateSlotId,
        selectedTimeSlot,
        selectedTimeSlotId,
      })
    );
  }, [selectedDate, selectedDateSlotId, selectedTimeSlot, selectedTimeSlotId, storageKey]);

  const setSelectedDate = (date, dateSlotId = null) => {
    const normalizedDate = date ? (date instanceof Date ? date : new Date(date)) : null;

    setSelectedDateState(normalizedDate);
    setSelectedDateSlotId(dateSlotId ?? date?.id ?? null);

    // Clear any previously selected time slot when picking a new date
    setSelectedTimeSlotState(null);
    setSelectedTimeSlotId(null);
  };

  const setSelectedTimeSlot = (timeSlot, timeSlotId = null) => {
    setSelectedTimeSlotState(timeSlot);
    setSelectedTimeSlotId(timeSlotId ?? timeSlot?.id ?? null);
  };

  const resetBooking = () => {
    setSelectedDateState(null);
    setSelectedDateSlotId(null);
    setSelectedTimeSlotState(null);
    setSelectedTimeSlotId(null);

    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(storageKey);
    }
  };

  return (
    <BookingContext.Provider
      value={{
        selectedDate,
        selectedDateSlotId,
        setSelectedDate,
        selectedTimeSlot,
        selectedTimeSlotId,
        setSelectedTimeSlot,
        resetBooking,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
};

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (!context) {
    console.warn('useBooking called outside of BookingProvider - using fallback defaults.');
    return {
      selectedDate: null,
      selectedDateSlotId: null,
      selectedTimeSlot: null,
      selectedTimeSlotId: null,
      setSelectedDate: () => {},
      setSelectedTimeSlot: () => {},
      resetBooking: () => {}
    };
  }
  return context;
};
